/**
 * GA4 ecommerce dataLayer helpers for the event booking flow.
 *
 * Rules enforced here:
 *  - window.dataLayer is initialised before every push.
 *  - { ecommerce: null } is pushed before every ecommerce event (all except
 *    'search') to prevent data pollution between events.
 *  - Every exported function is wrapped in try/catch and is non-blocking —
 *    a tracking failure must never break the booking flow.
 */

import { resolveAirlineName } from "@/lib/airlines"
import type { FlightOptionView, TravelerInfo } from "@/types/booking"
import type { PaymentOption } from "@/lib/api/types"

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface GA4FlightItem {
  item_id: number
  item_name: string
  item_brand: string
  item_category: "Flights"
  item_category2: string
  price: number
  google_business_vertical: "flights"
  quantity: number
  item_list_id?: string
  item_list_name?: string
}

/** Snapshot persisted to sessionStorage before the Stripe redirect. */
export interface PurchasePayload {
  bookingId: string
  currency: string
  value: number
  flight: FlightOptionView
  eventSlug: string
  eventName: string
  passengerCount: number
  basePackagePrice: number
  travelerInfo: TravelerInfo
  paymentOption: PaymentOption
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Effective price per person for a flight option. */
function flightPrice(flight: FlightOptionView, basePackagePrice: number): number {
  return Math.max(basePackagePrice + flight.priceDiffPerPerson, 0)
}

/**
 * Build a single GA4 item object for a flight option.
 *
 * @param includeListFields  When true, item_list_id and item_list_name are
 *                           embedded inside the item (required by begin_checkout
 *                           and purchase per spec).
 */
function buildFlightItem(
  flight: FlightOptionView,
  basePackagePrice: number,
  passengerCount: number,
  eventSlug: string,
  eventName: string,
  includeListFields = false
): GA4FlightItem {
  const item: GA4FlightItem = {
    item_id: flight.pelijeeFlightId,
    item_name:
      flight.outbound.originAirportCode +
      "-" +
      flight.outbound.destinationAirportCode,
    item_brand: resolveAirlineName(flight.outbound.airline[0] ?? ""),
    item_category: "Flights",
    item_category2: eventName,
    price: flightPrice(flight, basePackagePrice),
    google_business_vertical: "flights",
    quantity: passengerCount,
  }

  if (includeListFields) {
    item.item_list_id = eventSlug
    item.item_list_name = eventName
  }

  return item
}

/** Safely access (and initialise) window.dataLayer. */
function getDataLayer(): unknown[] {
  const w = window as unknown as { dataLayer?: unknown[] }
  w.dataLayer = w.dataLayer || []
  return w.dataLayer
}

// ---------------------------------------------------------------------------
// Event 1 — search
// No ecommerce object; no preceding { ecommerce: null } clear per spec.
// ---------------------------------------------------------------------------

export function pushSearch(slug: string): void {
  try {
    getDataLayer().push({ event: "search", search_term: slug })
  } catch {
    // non-blocking
  }
}

// ---------------------------------------------------------------------------
// Event 2 — view_item_list
// ---------------------------------------------------------------------------

export function pushViewItemList(
  flights: FlightOptionView[],
  basePackagePrice: number,
  passengerCount: number,
  currency: string,
  eventSlug: string,
  eventName: string
): void {
  try {
    const dl = getDataLayer()
    dl.push({ ecommerce: null })
    dl.push({
      event: "view_item_list",
      ecommerce: {
        currency,
        item_list_id: eventSlug,
        item_list_name: eventName,
        items: flights.map((f) =>
          buildFlightItem(f, basePackagePrice, passengerCount, eventSlug, eventName)
        ),
      },
    })
  } catch {
    // non-blocking
  }
}

// ---------------------------------------------------------------------------
// Event 3 — select_item
// ---------------------------------------------------------------------------

export function pushSelectItem(
  flight: FlightOptionView,
  basePackagePrice: number,
  passengerCount: number,
  currency: string,
  eventSlug: string,
  eventName: string
): void {
  try {
    const dl = getDataLayer()
    dl.push({ ecommerce: null })
    dl.push({
      event: "select_item",
      ecommerce: {
        currency,
        item_list_id: eventSlug,
        item_list_name: eventName,
        items: [
          buildFlightItem(flight, basePackagePrice, passengerCount, eventSlug, eventName),
        ],
      },
    })
  } catch {
    // non-blocking
  }
}

// ---------------------------------------------------------------------------
// Event 4 — begin_checkout
// ---------------------------------------------------------------------------

export function pushBeginCheckout(
  flight: FlightOptionView,
  basePackagePrice: number,
  passengerCount: number,
  currency: string,
  value: number,
  eventSlug: string,
  eventName: string
): void {
  try {
    const dl = getDataLayer()
    dl.push({ ecommerce: null })
    dl.push({
      event: "begin_checkout",
      ecommerce: {
        currency,
        value,
        items: [
          buildFlightItem(
            flight,
            basePackagePrice,
            passengerCount,
            eventSlug,
            eventName,
            true // embed item_list_id + item_list_name inside item
          ),
        ],
      },
    })
  } catch {
    // non-blocking
  }
}

// ---------------------------------------------------------------------------
// Event 5 — purchase
// Fired on the success page from data persisted in sessionStorage.
// ---------------------------------------------------------------------------

const SESSION_KEY_PAYLOAD = "ga4_purchase_payload"
const firedKey = (bookingId: string) => `ga4_purchase_fired:${bookingId}`

/**
 * Persist the purchase payload to sessionStorage immediately before the
 * Stripe redirect so it survives the external round-trip.
 */
export function savePurchasePayload(data: PurchasePayload): void {
  try {
    sessionStorage.setItem(SESSION_KEY_PAYLOAD, JSON.stringify(data))
  } catch {
    // non-blocking
  }
}

/**
 * Called once on the BookingSuccess page mount.
 * Reads the persisted payload and fires 'purchase' (GA4) and then calls
 * onFired (PostHog) exactly once per bookingId, guarding against re-fires
 * when the user refreshes the confirmation page.
 *
 * Ordering guarantee (both or neither — flag is set only after both fire):
 *   1. read payload
 *   2. check fired flag  →  return early if already set
 *   3. push { ecommerce: null }
 *   4. push GA4 'purchase' event
 *   5. call onFired(data)          ← PostHog payment_confirmed (optional)
 *   6. sessionStorage.setItem(firedKey, "1")
 */
export function firePendingPurchase(
  onFired?: (data: PurchasePayload) => void
): void {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY_PAYLOAD)
    if (!raw) return

    const data = JSON.parse(raw) as PurchasePayload
    const { bookingId, currency, value, flight, eventSlug, eventName,
            passengerCount, basePackagePrice, travelerInfo } = data

    // Deduplication guard — skip both GA4 and PostHog if already fired.
    if (sessionStorage.getItem(firedKey(bookingId))) return

    // Step 3+4: GA4 purchase
    const dl = getDataLayer()
    dl.push({ ecommerce: null })
    dl.push({
      event: "purchase",
      ecommerce: {
        transaction_id: bookingId,
        currency,
        value,
        tax: 0,
        shipping: 0,
        items: [
          buildFlightItem(
            flight,
            basePackagePrice,
            passengerCount,
            eventSlug,
            eventName,
            true // embed item_list_id + item_list_name inside item
          ),
        ],
      },
      user: {
        email: travelerInfo.email,
        first_name: travelerInfo.firstName,
        last_name: travelerInfo.lastName,
        phone_number: travelerInfo.phone,
        street: null,
        city: null,
        postal_code: null,
        country: null,
      },
    })

    // Step 5: PostHog (or any other system) — called after GA4, before flag
    onFired?.(data)

    // Step 6: mark as fired — only reached if both steps above completed
    sessionStorage.setItem(firedKey(bookingId), "1")
  } catch {
    // non-blocking
  }
}
